/**
 * Shared API client with auth token injection and error interceptors.
 *
 * Usage:
 *   import { apiClient } from '../utils/apiClient';
 *   const data = await apiClient.get('/api/customers');
 *   await apiClient.post('/api/customers', { name: 'Acme' });
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

type RequestOptions = Omit<RequestInit, 'method' | 'body'> & {
  /** Skip automatic JSON parsing of response */
  raw?: boolean;
  /** Skip auth header injection */
  noAuth?: boolean;
};

interface ApiError {
  status: number;
  statusText: string;
  message: string;
  body?: unknown;
}

function getToken(): string | null {
  return localStorage.getItem('riskninja-token');
}

function buildHeaders(options?: RequestOptions): Headers {
  const headers = new Headers(options?.headers);

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!options?.noAuth) {
    const token = getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  return headers;
}

async function handleResponse<T>(response: Response, raw?: boolean): Promise<T> {
  if (response.ok) {
    if (raw) return response as unknown as T;
    const text = await response.text();
    if (!text) return undefined as unknown as T;
    try {
      return JSON.parse(text);
    } catch {
      return text as unknown as T;
    }
  }

  // Handle error responses
  let errorBody: unknown;
  try {
    errorBody = await response.json();
  } catch {
    errorBody = await response.text().catch(() => null);
  }

  const error: ApiError = {
    status: response.status,
    statusText: response.statusText,
    message: (errorBody as any)?.error || (errorBody as any)?.message || response.statusText,
    body: errorBody,
  };

  if (response.status === 401) {
    // Token expired or invalid - dispatch event so AuthContext can handle refresh/logout
    window.dispatchEvent(new CustomEvent('api:unauthorized', { detail: error }));
  }

  if (response.status === 403) {
    window.dispatchEvent(new CustomEvent('api:forbidden', { detail: error }));
  }

  if (process.env.NODE_ENV === 'development') {
    console.error(`[apiClient] ${response.status} ${response.statusText}`, error);
  }

  throw error;
}

async function request<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const headers = buildHeaders(options);

  // Don't set Content-Type for FormData (browser sets multipart boundary)
  if (body instanceof FormData) {
    headers.delete('Content-Type');
  }

  if (process.env.NODE_ENV === 'development') {
    console.debug(`[apiClient] ${method} ${path}`);
  }

  const response = await fetch(url, {
    ...options,
    method,
    headers,
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  return handleResponse<T>(response, options?.raw);
}

export const apiClient = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, undefined, options),

  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),

  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),

  patch: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),

  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
};

export type { ApiError, RequestOptions };
